import React, { useState, useEffect } from "react";
import { X, Save, DollarSign, MessageSquare, Plus, Minus } from "lucide-react";
import "../styles/SalaryEditModal.css";

const SalaryEditModal = ({ isOpen, onClose, salary, onUpdate }) => {
  const [toPay, setToPay] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (salary) {
      const total =
        (salary.to_pay || 0) + (salary.extras || 0) - (salary.subtract || 0);
      setToPay(total.toFixed(2));
      setNotes(salary.employee_notes || "");
    }
  }, [salary]);

  if (!isOpen || !salary) return null;

  // Get extras and deductions from extras_details
  const extrasItems =
    salary.extras_details?.items?.filter((item) => item.type === "extra") || [];
  const deductionItems =
    salary.extras_details?.items?.filter((item) => item.type === "deduction") ||
    [];

  const handleSave = async () => {
    setSaving(true);
    try {
      await onUpdate(salary, {
        to_pay: parseFloat(toPay),
        notes: notes,
      });
      onClose();
    } catch (error) {
      console.error("Failed to save:", error);
      alert("שגיאה בשמירת הנתונים");
    } finally {
      setSaving(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className='salary-edit-modal-overlay' onClick={handleOverlayClick}>
      <div className='salary-edit-modal'>
        <div className='salary-edit-modal-header'>
          <h2>עריכת משכורת - {salary.employee_name}</h2>
          <button className='close-btn' onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className='salary-edit-modal-body'>
          <div className='salary-info-summary'>
            <div className='info-item'>
              <span className='info-label'>אתר:</span>
              <span className='info-value'>{salary.site_name || "-"}</span>
            </div>
            <div className='info-item'>
              <span className='info-label'>סוג עובד:</span>
              <span className='info-value'>{salary.employee_type || "-"}</span>
            </div>
            <div className='info-item'>
              <span className='info-label'>שעות רגילות:</span>
              <span className='info-value'>
                {salary.total_regular_hours?.toFixed(2) || "0.00"}
              </span>
            </div>
            <div className='info-item'>
              <span className='info-label'>שעות סופ״ש:</span>
              <span className='info-value'>
                {salary.total_weekend_hours?.toFixed(2) || "0.00"}
              </span>
            </div>
          </div>

          {/* Extras Section */}
          <div className='extras-section'>
            <h4>
              <Plus size={16} />
              תוספות
            </h4>
            <div className='extras-list'>
              {extrasItems.length > 0 ? (
                <>
                  {extrasItems.map((item, index) => (
                    <div key={item.id || index} className='extras-item extra'>
                      <span className='item-comment'>{item.comment}</span>
                      <span className='item-amount'>
                        ₪{parseFloat(item.amount).toLocaleString("he-IL")}
                      </span>
                    </div>
                  ))}
                  <div className='extras-total extra'>
                    <span>סה״כ תוספות:</span>
                    <span>₪{(salary.extras || 0).toLocaleString("he-IL")}</span>
                  </div>
                </>
              ) : (
                <div className='no-items'>אין תוספות</div>
              )}
            </div>
          </div>

          {/* Deductions Section */}
          <div className='extras-section'>
            <h4>
              <Minus size={16} />
              ניכויים
            </h4>
            <div className='extras-list'>
              {deductionItems.length > 0 ? (
                <>
                  {deductionItems.map((item, index) => (
                    <div
                      key={item.id || index}
                      className='extras-item deduction'
                    >
                      <span className='item-comment'>{item.comment}</span>
                      <span className='item-amount'>
                        ₪{parseFloat(item.amount).toLocaleString("he-IL")}
                      </span>
                    </div>
                  ))}
                  <div className='extras-total deduction'>
                    <span>סה״כ ניכויים:</span>
                    <span>
                      ₪{(salary.subtract || 0).toLocaleString("he-IL")}
                    </span>
                  </div>
                </>
              ) : (
                <div className='no-items'>אין ניכויים</div>
              )}
            </div>
          </div>

          <div className='edit-field'>
            <label>
              <DollarSign size={18} />
              סה״כ לתשלום
            </label>
            <div className='input-wrapper'>
              <span className='currency'>₪</span>
              <input
                type='number'
                step='0.01'
                value={toPay}
                onChange={(e) => setToPay(e.target.value)}
                placeholder='הזן סכום'
              />
            </div>
          </div>

          <div className='edit-field'>
            <label>
              <MessageSquare size={18} />
              הערות
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder='הוסף הערות...'
              rows={4}
            />
          </div>
        </div>

        <div className='salary-edit-modal-footer'>
          <button className='cancel-btn' onClick={onClose} disabled={saving}>
            ביטול
          </button>
          <button className='save-btn' onClick={handleSave} disabled={saving}>
            <Save size={18} />
            {saving ? "שומר..." : "שמור שינויים"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SalaryEditModal;
